import type { ItemAdicionarComanda, ProdutoCatalogo } from "@/api"
import type { CartItem, CartPasso } from "@/store/self-service-kiosk-slice"
import { idLocal } from "@/lib/id"
import { assinaturaItem, montarPassos, precoUnitario, type SelecoesPassos } from "@/components/catalogo-kiosk/passos"

export function complementosAchatados(item: CartItem): CartPasso['itens'] {
    return item.passos.flatMap((p) => p.itens)
}

export function itemParaPayload(item: CartItem): ItemAdicionarComanda {
    return {
        idProduto: item.idProduto,
        nome: item.nome,
        quantidade: item.quantidade,
        valorUnitario: item.valorUnitario,
        valorTotal: item.valorTotal,
        observacao: item.observacao?.trim() || undefined,
        passos: item.passos.map((p) => ({
            idProdutoPasso: p.idProdutoPasso,
            descricao: p.descricao,
            valorPasso: p.valorPasso,
            itens: p.itens.map((it) => ({
                idTabelaPrecoItem: it.idTabelaPrecoItem,
                idProduto: it.idProduto,
                nome: it.nome,
                quantidade: it.quantidade,
                valor: it.valor,
            })),
        })),
    }
}

interface AplicarItemParams {
    produto: ProdutoCatalogo
    selecoes: SelecoesPassos
    cartItems: CartItem[]
    editingCartItem: CartItem | null
    /** Quantidade escolhida no detalhe do produto; pelo botão do cartão é 1. Na edição não se aplica. */
    quantidade?: number
}

export function aplicarItemNoCarrinho({ produto, selecoes, cartItems, editingCartItem, quantidade = 1 }: AplicarItemParams): CartItem[] {
    const passosCart = montarPassos(produto, selecoes)
    const unit = precoUnitario(produto, passosCart)
    const assinatura = assinaturaItem(produto.id, passosCart)

    if (editingCartItem) {
        let atualizado = cartItems.map((cartItem) => {
            if (cartItem.id !== editingCartItem.id) return cartItem

            return {
                ...cartItem,
                passos: passosCart,
                valorUnitario: unit,
                valorTotal: Number((cartItem.quantidade * unit).toFixed(2)),
            }
        })

        // Observação diferente é pedido diferente: juntar as linhas daria a nota de um para o outro.
        const gemeo = atualizado.find((cartItem) =>
            cartItem.id !== editingCartItem.id
            && cartItem.temPassos
            && (cartItem.observacao ?? '') === (editingCartItem.observacao ?? '')
            && assinaturaItem(cartItem.idProduto, cartItem.passos) === assinatura
        )

        if (gemeo) {
            const quantidadeSomada = editingCartItem.quantidade + gemeo.quantidade

            atualizado = atualizado
                .map((cartItem) => {
                    if (cartItem.id !== gemeo.id) return cartItem

                    return {
                        ...cartItem,
                        quantidade: quantidadeSomada,
                        valorTotal: Number((quantidadeSomada * cartItem.valorUnitario).toFixed(2)),
                    }
                })
                .filter((cartItem) => cartItem.id !== editingCartItem.id)
        }

        return atualizado
    }

    // O item novo chega sem observação: só soma numa linha que também não tenha.
    const existente = cartItems.find((cartItem) =>
        cartItem.temPassos
        && !cartItem.observacao
        && assinaturaItem(cartItem.idProduto, cartItem.passos) === assinatura
    )

    if (existente) {
        return cartItems.map((cartItem) => {
            if (cartItem.id !== existente.id) return cartItem

            return {
                ...cartItem,
                quantidade: cartItem.quantidade + quantidade,
                valorTotal: Number(((cartItem.quantidade + quantidade) * cartItem.valorUnitario).toFixed(2)),
            }
        })
    }

    const novo: CartItem = {
        id: idLocal(),
        idProduto: produto.id,
        nome: produto.descricao,
        quantidade,
        valorUnitario: unit,
        valorTotal: Number((quantidade * unit).toFixed(2)),
        temPassos: true,
        passos: passosCart,
    }

    return [...cartItems, novo]
}
